import React, { useEffect, useId, useRef, useState } from "react";
import { Box, Button, Paper, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getTacitsBalance,
  getTacitsDailyForCompany,
  TacitsBalance,
  DailyTacitsEntry,
} from "../../api/pricing";
import { useGlobalTheme } from "../../context/GlobalThemeContext";

function formatNumber(n: number): string {
  return Math.round(n).toLocaleString("pt-BR");
}

// ── Theme factory ───────────────────────────────────────────────────────────
function makeTheme(isDark: boolean) {
  return {
    surface: isDark ? "rgba(18,18,18,0.99)" : "#ffffff",
    surfaceAlt: isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.018)",
    border: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.09)",
    textPrimary: isDark ? "#f0f0f0" : "#111827",
    textMuted: isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.42)",
    skeletonBg: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.07)",
    planBg: isDark ? "rgba(139,92,246,0.12)" : "rgba(109,40,217,0.08)",
    planColor: isDark ? "#a78bfa" : "#6d28d9",
    blueAccent: isDark ? "#a78bfa" : "#6d28d9",
    goldAccent: isDark ? "#fbbf24" : "#92600a",
    greenAccent: isDark ? "#4ade80" : "#16a34a",
    gradBlue: isDark ? "rgba(167,139,250,0.10)" : "rgba(109,40,217,0.06)",
    gradGold: isDark ? "rgba(251,191,36,0.12)" : "rgba(234,179,8,0.08)",
    gradGreen: isDark ? "rgba(74,222,128,0.10)" : "rgba(22,163,74,0.07)",
    gradMuted: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
  };
}

// ── Tacits Coin Icon ────────────────────────────────────────────────────────
const TacitsCoinIcon: React.FC = () => {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        <radialGradient id={`coinGrad_${id}`} cx="40%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#ffe066" />
          <stop offset="55%" stopColor="#f7d84a" />
          <stop offset="100%" stopColor="#b07800" />
        </radialGradient>
        <radialGradient id={`coinInner_${id}`} cx="40%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#fffbe0" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#f7d84a" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Coin body */}
      <circle cx="14" cy="14" r="13" fill={`url(#coinGrad_${id})`} />
      {/* Inner shine */}
      <circle
        cx="14"
        cy="14"
        r="11"
        fill={`url(#coinInner_${id})`}
        opacity="0.4"
      />
      {/* Edge ring */}
      <circle
        cx="14"
        cy="14"
        r="12.5"
        fill="none"
        stroke="#b07800"
        strokeWidth="0.8"
        opacity="0.6"
      />
      {/* Letter T */}
      <text
        x="14"
        y="19"
        textAnchor="middle"
        fontSize="13"
        fontWeight="900"
        fill="#7a4e00"
        fontFamily="inherit"
        letterSpacing="-0.5"
      >
        T
      </text>
    </svg>
  );
};

// ── SVG Donut Gauge ────────────────────────────────────────────────────────
interface DonutGaugeProps {
  pct: number;
  isDark: boolean;
}

const DonutGauge: React.FC<DonutGaugeProps> = ({ pct, isDark }) => {
  const id = useId().replace(/:/g, "");
  const r = 80;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const filled = (clamped / 100) * circ;
  const gap = circ - filled;

  const isRed = clamped >= 90;
  const isAmber = clamped >= 70 && clamped < 90;

  const strokeColor = isRed
    ? `url(#gaugeRed_${id})`
    : isAmber
      ? `url(#gaugeAmber_${id})`
      : `url(#gaugeGold_${id})`;

  const glowColor = isRed
    ? "rgba(248,113,113,0.35)"
    : isAmber
      ? "rgba(251,146,60,0.35)"
      : "rgba(247,216,74,0.35)";

  return (
    <svg
      width="200"
      height="200"
      viewBox="0 0 200 200"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient
          id={`gaugeGold_${id}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#f7d84a" />
          <stop offset="100%" stopColor="#e8a800" />
        </linearGradient>
        <linearGradient
          id={`gaugeAmber_${id}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#fb923c" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>
        <linearGradient
          id={`gaugeRed_${id}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#f87171" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
        <filter
          id={`glowFilter_${id}`}
          x="-30%"
          y="-30%"
          width="160%"
          height="160%"
        >
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feFlood floodColor={glowColor} result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id={`innerGlow_${id}`} cx="50%" cy="50%" r="50%">
          <stop
            offset="0%"
            stopColor={
              isDark ? "rgba(247,216,74,0.07)" : "rgba(247,216,74,0.04)"
            }
          />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>

      {/* Inner glow fill */}
      <circle cx="100" cy="100" r={r - 9} fill={`url(#innerGlow_${id})`} />

      {/* Track ring */}
      <circle
        cx="100"
        cy="100"
        r={r}
        fill="none"
        stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}
        strokeWidth="18"
      />

      {/* Filled arc — starts at top (offset = circ*0.25) */}
      <circle
        cx="100"
        cy="100"
        r={r}
        fill="none"
        stroke={strokeColor}
        strokeWidth="18"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${gap}`}
        strokeDashoffset={circ * 0.25}
        style={{
          transition: "stroke-dasharray 0.9s cubic-bezier(0.4,0,0.2,1)",
          filter: `drop-shadow(0 0 6px ${glowColor})`,
        }}
      />

      {/* Percentage label */}
      <text
        x="100"
        y="93"
        textAnchor="middle"
        fontSize="36"
        fontWeight="800"
        letterSpacing="-2"
        fill={isDark ? "#ffffff" : "#1a1a2e"}
        fontFamily="inherit"
      >
        {Math.round(clamped)}%
      </text>
      <text
        x="100"
        y="116"
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
        letterSpacing="1"
        fill={isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.45)"}
        fontFamily="inherit"
        style={{ textTransform: "uppercase" }}
      >
        CONSUMIDO
      </text>
    </svg>
  );
};

// ── Metric Item ─────────────────────────────────────────────────────────────
interface MetricItemProps {
  label: string;
  value: string;
  accentColor: string;
  bgColor: string;
  border: string;
}

const MetricItem: React.FC<MetricItemProps> = ({
  label,
  value,
  accentColor,
  bgColor,
  border,
}) => (
  <Box
    sx={{
      backgroundColor: bgColor,
      border: `1px solid ${border}`,
      borderRadius: "14px",
      p: "14px 16px",
      transition: "transform 0.18s, box-shadow 0.18s",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 4px 16px ${accentColor}22`,
      },
    }}
  >
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 800,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: accentColor,
        opacity: 0.7,
        lineHeight: 1,
        mb: 0.6,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "1.55rem",
        fontWeight: 900,
        color: accentColor,
        letterSpacing: "-0.03em",
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ── Daily Bar Chart ────────────────────────────────────────────────────────
interface DailyBarChartProps {
  data: DailyTacitsEntry[];
  isDark: boolean;
  loading: boolean;
}

const CHART_W = 600;
const CHART_H = 160;
const PAD_LEFT = 48;
const PAD_RIGHT = 8;
const PAD_TOP = 12;
const PAD_BOTTOM = 32;
const BAR_AREA_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const BAR_AREA_H = CHART_H - PAD_TOP - PAD_BOTTOM;

function fmtDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

const DailyBarChart: React.FC<DailyBarChartProps> = ({
  data,
  isDark,
  loading,
}) => {
  const id = useId().replace(/:/g, "");
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const maxVal =
    data.length > 0 ? Math.max(...data.map((d) => d.tacits), 1) : 1;
  const total = data.reduce((s, d) => s + d.tacits, 0);
  const avg = data.length > 0 ? total / data.length : 0;

  const n = data.length || 1;
  const barW = Math.max(2, BAR_AREA_W / n - 2);
  const gap = (BAR_AREA_W - barW * n) / (n - 1 || 1);

  const barX = (i: number) => PAD_LEFT + i * (barW + gap);
  const barH = (v: number) => Math.max(2, (v / maxVal) * BAR_AREA_H);
  const barY = (v: number) => PAD_TOP + BAR_AREA_H - barH(v);

  const avgY = PAD_TOP + BAR_AREA_H - (avg / maxVal) * BAR_AREA_H;

  // Y-axis labels: 0, half, max
  const yLabels = [
    { val: 0, y: PAD_TOP + BAR_AREA_H },
    { val: Math.round(maxVal / 2), y: PAD_TOP + BAR_AREA_H / 2 },
    { val: Math.round(maxVal), y: PAD_TOP },
  ];

  const textMuted = isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.40)";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
  const barFill = `url(#barGrad_${id})`;
  const barFillZero = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={200}
        sx={{
          bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.07)",
          borderRadius: "12px",
        }}
      />
    );
  }

  const allZero = data.every((d) => d.tacits === 0);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {allZero ? (
        <Box
          sx={{
            height: 160,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: textMuted, fontSize: "0.85rem" }}>
            Sem consumo no período
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              borderRadius: "12px",
              background: isDark
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.015)",
              p: "8px 4px 4px",
            }}
          >
            <svg
              ref={svgRef}
              viewBox={`0 0 ${CHART_W} ${CHART_H}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ width: "100%", display: "block", overflow: "visible" }}
            >
              <defs>
                <linearGradient
                  id={`barGrad_${id}`}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#f7d84a" />
                  <stop offset="100%" stopColor="#e8a800" />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              {yLabels.slice(1).map(({ y }, i) => (
                <line
                  key={i}
                  x1={PAD_LEFT}
                  y1={y}
                  x2={CHART_W - PAD_RIGHT}
                  y2={y}
                  stroke={gridColor}
                  strokeDasharray="4 3"
                />
              ))}

              {/* Y-axis labels */}
              {yLabels.map(({ val, y }) => (
                <text
                  key={val}
                  x={PAD_LEFT - 6}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="9"
                  fill={textMuted}
                  fontFamily="inherit"
                >
                  {val >= 1000 ? `${Math.round(val / 1000)}k` : val}
                </text>
              ))}

              {/* Bars */}
              {data.map((entry, i) => {
                const isZero = entry.tacits === 0;
                return (
                  <rect
                    key={entry.date}
                    x={barX(i)}
                    y={barY(entry.tacits)}
                    width={barW}
                    height={barH(entry.tacits)}
                    rx="3"
                    fill={isZero ? barFillZero : barFill}
                    opacity={isZero ? 0.4 : hoveredIdx === i ? 1 : 0.85}
                    style={{ transition: "opacity 0.15s" }}
                  />
                );
              })}

              {/* Average line */}
              {avg > 0 && (
                <line
                  x1={PAD_LEFT}
                  y1={avgY}
                  x2={CHART_W - PAD_RIGHT}
                  y2={avgY}
                  stroke={
                    isDark ? "rgba(247,216,74,0.4)" : "rgba(232,168,0,0.35)"
                  }
                  strokeDasharray="5 4"
                  strokeWidth="1"
                />
              )}

              {/* X-axis labels every ~5 days */}
              {data.map((entry, i) => {
                if (i % 5 !== 0) return null;
                return (
                  <text
                    key={entry.date}
                    x={barX(i) + barW / 2}
                    y={PAD_TOP + BAR_AREA_H + 14}
                    textAnchor="middle"
                    fontSize="8"
                    fill={textMuted}
                    fontFamily="inherit"
                  >
                    {fmtDate(entry.date)}
                  </text>
                );
              })}

              {/* Hover overlay rects */}
              {data.map((entry, i) => (
                <rect
                  key={`hover_${entry.date}`}
                  x={barX(i) - 1}
                  y={PAD_TOP}
                  width={barW + 2}
                  height={BAR_AREA_H}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    setHoveredIdx(i);
                    const svg = svgRef.current;
                    if (!svg) return;
                    const rect = svg.getBoundingClientRect();
                    const scaleX = rect.width / CHART_W;
                    const scaleY = rect.height / CHART_H;
                    setTooltipPos({
                      x: (barX(i) + barW / 2) * scaleX,
                      y: barY(entry.tacits) * scaleY - 10,
                    });
                  }}
                  onMouseLeave={() => setHoveredIdx(null)}
                />
              ))}
            </svg>
          </Box>

          {/* Tooltip */}
          {hoveredIdx !== null && data[hoveredIdx] && (
            <Box
              sx={{
                position: "absolute",
                left: tooltipPos.x,
                top: tooltipPos.y,
                transform: "translate(-50%, -100%)",
                pointerEvents: "none",
                backgroundColor: isDark
                  ? "rgba(30,30,30,0.95)"
                  : "rgba(255,255,255,0.97)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                borderRadius: "8px",
                px: 1.2,
                py: 0.7,
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                whiteSpace: "nowrap",
                zIndex: 10,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
                  lineHeight: 1.2,
                }}
              >
                {fmtDate(data[hoveredIdx].date)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  color: isDark ? "#f7d84a" : "#b07800",
                  lineHeight: 1.4,
                }}
              >
                {formatNumber(data[hoveredIdx].tacits)} tacits
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────
export default function TacitsPage() {
  const isDark = useGlobalTheme();
  const navigate = useNavigate();
  const [balance, setBalance] = useState<TacitsBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState<DailyTacitsEntry[]>([]);
  const [dailyLoading, setDailyLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getTacitsBalance()
      .then((data) => {
        if (!cancelled) {
          setBalance(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const companyId: string | undefined = authUser?.company_id;
      if (!companyId) {
        setDailyLoading(false);
        return;
      }
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      const fmt = (d: Date) => d.toISOString().split("T")[0];
      getTacitsDailyForCompany(companyId, fmt(start), fmt(end))
        .then((entries) => {
          if (!cancelled) {
            setDailyData(entries);
            setDailyLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setDailyLoading(false);
        });
    } catch {
      setDailyLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const pct =
    balance && balance.allocated > 0
      ? (balance.used / balance.allocated) * 100
      : 0;

  const t = makeTheme(isDark);

  const dailyTotal = dailyData.reduce((s, d) => s + d.tacits, 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 660, mx: "auto" }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <TacitsCoinIcon />
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: t.textPrimary,
                fontWeight: 800,
                fontSize: "1.35rem",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Meus Tacits
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: t.textMuted, mt: 0.4, fontSize: "0.8rem" }}
            >
              Consumo da sua empresa no mês atual
            </Typography>
          </Box>
        </Box>

        {/* Plan badge */}
        {loading ? (
          <Skeleton
            variant="rounded"
            width={80}
            height={28}
            sx={{ bgcolor: t.skeletonBg, borderRadius: "20px", flexShrink: 0 }}
          />
        ) : (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.6,
              borderRadius: "20px",
              backgroundColor: t.planBg,
              border: `1px solid ${t.planColor}44`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: t.planColor,
              }}
            />
            <Typography
              sx={{
                color: t.planColor,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
              }}
            >
              {balance?.plan ?? "—"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Hero card: gauge + metrics ── */}
      <Paper
        elevation={0}
        sx={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "20px",
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        {/* Top gradient bar */}
        <Box
          sx={{
            height: "3px",
            background:
              "linear-gradient(90deg,#a78bfa 0%,#f7d84a 50%,#4ade80 100%)",
            opacity: 0.7,
          }}
        />

        <Box
          sx={{
            p: { xs: 2.5, md: 3.5 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Donut gauge */}
          {loading ? (
            <Skeleton
              variant="circular"
              width={200}
              height={200}
              sx={{ bgcolor: t.skeletonBg, flexShrink: 0 }}
            />
          ) : (
            <Box sx={{ flexShrink: 0 }}>
              <DonutGauge pct={pct} isDark={isDark} />
            </Box>
          )}

          {/* Metrics 2×2 grid + CTA */}
          <Box sx={{ flex: 1, width: "100%" }}>
            {loading ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.5,
                }}
              >
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={72}
                    sx={{ bgcolor: t.skeletonBg, borderRadius: "14px" }}
                  />
                ))}
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <MetricItem
                    label="Alocado (plano)"
                    value={formatNumber(balance?.allocated ?? 0)}
                    accentColor={t.blueAccent}
                    bgColor={t.gradBlue}
                    border={t.border}
                  />
                  <MetricItem
                    label="Créditos Extras"
                    value={formatNumber(balance?.manual_credits ?? 0)}
                    accentColor={t.goldAccent}
                    bgColor={t.gradGold}
                    border={t.border}
                  />
                  <MetricItem
                    label="Consumido"
                    value={formatNumber(balance?.used ?? 0)}
                    accentColor={t.textMuted}
                    bgColor={t.gradMuted}
                    border={t.border}
                  />
                  <MetricItem
                    label="Restante"
                    value={formatNumber(balance?.remaining ?? 0)}
                    accentColor={t.greenAccent}
                    bgColor={t.gradGreen}
                    border={t.border}
                  />
                </Box>
                <Button
                  fullWidth
                  onClick={() =>
                    navigate("/settings/credits", {
                      state: { fromTacits: true },
                    })
                  }
                  sx={{
                    background: "linear-gradient(90deg,#f7d84a,#e8a800)",
                    color: "#5a3400",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    textTransform: "none",
                    py: 1.1,
                    letterSpacing: "0.01em",
                    boxShadow: "0 2px 12px rgba(247,216,74,0.3)",
                    "&:hover": {
                      background: "linear-gradient(90deg,#ffe566,#f0b800)",
                      boxShadow: "0 4px 18px rgba(247,216,74,0.45)",
                    },
                  }}
                >
                  Comprar mais créditos →
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* ── Daily bar chart card ── */}
      <Paper
        elevation={0}
        sx={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "20px",
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        {/* Top gradient bar */}
        <Box
          sx={{
            height: "3px",
            background:
              "linear-gradient(90deg,#a78bfa 0%,#f7d84a 50%,#4ade80 100%)",
            opacity: 0.7,
          }}
        />

        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          {/* Section header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: t.textPrimary,
                fontWeight: 800,
                fontSize: "0.95rem",
                letterSpacing: "-0.02em",
              }}
            >
              Consumo dos últimos 30 dias
            </Typography>
            {!dailyLoading && dailyData.length > 0 && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.2,
                  py: 0.4,
                  borderRadius: "20px",
                  backgroundColor: t.surfaceAlt,
                  border: `1px solid ${t.border}`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: t.textMuted,
                    fontSize: "0.71rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  {formatNumber(dailyTotal)} tacits
                </Typography>
              </Box>
            )}
          </Box>

          <DailyBarChart
            data={dailyData}
            isDark={isDark}
            loading={dailyLoading}
          />
        </Box>
      </Paper>

      {/* ── Info box ── */}
      <Paper
        elevation={0}
        sx={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Top gradient bar */}
        <Box
          sx={{
            height: "3px",
            background:
              "linear-gradient(90deg,#a78bfa 0%,#f7d84a 50%,#4ade80 100%)",
            opacity: 0.7,
          }}
        />

        <Box sx={{ p: 2.5 }}>
          {/* Title with inline info icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.9,
              mb: 1.2,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              style={{ display: "block", flexShrink: 0 }}
            >
              <circle
                cx="8"
                cy="8"
                r="7.25"
                fill="none"
                stroke={t.textMuted}
                strokeWidth="1.4"
              />
              <text
                x="8"
                y="12"
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                fill={t.textMuted}
                fontFamily="inherit"
              >
                i
              </text>
            </svg>
            <Typography
              sx={{
                color: t.textPrimary,
                fontSize: "0.85rem",
                fontWeight: 800,
                letterSpacing: "-0.01em",
              }}
            >
              O que são tacits?
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: t.textMuted, lineHeight: 1.8 }}
          >
            Tacits são a unidade de crédito que mede o uso de IA na plataforma.
            Cada interação com os modelos de linguagem consome tacits baseado no
            volume de tokens processados. Seu saldo é renovado mensalmente
            conforme o plano contratado.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
